import type { ActorAnimation } from './manifest.ts';

export type ActorClipName = 'idle' | 'walk' | 'attack' | 'recoil' | 'death' | 'corpse' | 'work' | 'receive' | 'bind' | 'shove';
export interface ActionWindow {
  phase: 'windup' | 'commit' | 'recovery';
  /** Authoritative phase progress, 0..1. The host freezes it with sim ticks. */
  progress: number;
  toward?: readonly [number, number];
  targetId?: string;
}

/** Resolve authored clip views; legacy sheets retain their five-view mapping. */
export function actorFrame(animation: ActorAnimation, clip: ActorClipName, facing: string, elapsed: number,
  distance: number, action?: ActionWindow, result = { frame: 0, flipX: false }): { frame: number; flipX: boolean } {
  const contact = clip === 'receive' || clip === 'bind' || clip === 'shove';
  const sequence = animation.clips[clip] ?? (contact ? animation.clips.attack : undefined) ?? animation.clips.idle!;
  const authored = sequence.facings?.[facing];
  const flipX = !authored && (facing === 'sw' || facing === 'w' || facing === 'nw');
  const sourceFacing = facing === 'sw' ? 'se' : facing === 'w' ? 'e' : facing === 'nw' ? 'ne' : facing;
  const row = Math.max(0, animation.walkFacings.indexOf(sourceFacing));
  const frames = authored ?? sequence.frames;
  let index: number;
  if (clip === 'walk') {
    // One planted stride per 32 plate pixels. A stationary pending move never
    // cycles its legs; changing host tick rate cannot make the feet run faster.
    index = Math.floor(distance / 32 * frames.length) % frames.length;
    result.frame = (authored ? 0 : row * 6) + frames[index]!; result.flipX = flipX; return result;
  }
  if (clip === 'idle' && !authored && sourceFacing !== 'se') {
    result.frame = row * 6 + 1; result.flipX = flipX; return result;
  }
  if (contact) {
    // Older identities still use their authored close guard and follow-through.
    // New contact sheets provide a planted pose for each of these three beats.
    index = animation.clips[clip] ? 0 : clip === 'receive' ? 0 : clip === 'bind' ? 2 : 4;
  } else if (clip === 'attack' && action) {
    const progress = Math.max(0, Math.min(1, action.progress));
    index = action.phase === 'windup' ? Math.min(2, Math.floor(progress * 3))
      : action.phase === 'commit' ? 3 : Math.min(5, 4 + Math.floor(progress * 2));
  } else {
    const cursor = Math.floor(Math.max(0, elapsed) / 1000 * sequence.fps);
    index = sequence.loop ? cursor % frames.length : Math.min(frames.length - 1, cursor);
  }
  result.frame = frames[index]!; result.flipX = flipX; return result;
}
